import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadCustomerByIdUrl,
  zadCustomerStatusUrl,
  zadCustomerUpdateUrl,
  zadCustomersMaarefatiSearchUrl,
  zadCustomersUrl,
} from '../../api'
import {shouldSearchZadMaarefati, toZadCustomerPayload} from '../zad/customerHelpers'
import {parseZadApiError} from '../zad/errors'
import {buildZadListQuery} from '../zad/query'
import {
  CreateZadCustomerRequest,
  FetchZadCustomerByIdRequest,
  FetchZadCustomersRequest,
  SearchZadMaarefatiRequest,
  UpdateZadCustomerRequest,
  UpdateZadCustomerStatusRequest,
} from './types'

export const fetchZadCustomers = createAsyncThunk(
  'zadCustomers/fetch',
  async (payload: FetchZadCustomersRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadCustomersUrl}?${buildZadListQuery(payload)}`)
      const {customers, total, totalPages} = res.data || {}
      return {
        success: true as const,
        customers: Array.isArray(customers) ? customers : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerById = createAsyncThunk(
  'zadCustomers/fetchById',
  async (payload: FetchZadCustomerByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerByIdUrl(payload.id))
      return {
        success: true as const,
        customer: res.data.customer,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadCustomer = createAsyncThunk(
  'zadCustomers/create',
  async (payload: CreateZadCustomerRequest, {rejectWithValue}) => {
    try {
      const body = toZadCustomerPayload(payload)
      const res = await axios.post(zadCustomersUrl, body)
      return {
        success: true as const,
        customer: res.data.customer,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadCustomer = createAsyncThunk(
  'zadCustomers/update',
  async (payload: UpdateZadCustomerRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = toZadCustomerPayload(values)
      const res = await axios.post(zadCustomerUpdateUrl(id), body)
      return {
        success: true as const,
        customer: res.data.customer,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadCustomerStatus = createAsyncThunk(
  'zadCustomers/updateStatus',
  async (payload: UpdateZadCustomerStatusRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCustomerStatusUrl(payload.id), {
        isActive: payload.isActive,
      })
      return {
        success: true as const,
        customer: res.data.customer,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const searchZadMaarefatiUsers = createAsyncThunk(
  'zadCustomers/maarefatiSearch',
  async (payload: SearchZadMaarefatiRequest, {rejectWithValue}) => {
    try {
      if (!shouldSearchZadMaarefati(payload.search)) {
        return {
          success: true as const,
          results: [],
          type: payload.type,
        }
      }
      const query = new URLSearchParams()
      query.set('search', payload.search.trim())
      query.set('type', payload.type)
      const res = await axios.get(`${zadCustomersMaarefatiSearchUrl}?${query.toString()}`)
      return {
        success: true as const,
        results: Array.isArray(res.data?.results) ? res.data.results : [],
        type: payload.type,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
